import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { HIGH_VOLUME_IMPORT_VEHICLE } from 'utils/constant';
import {
  formatDate,
  getReservationsByProductType,
  price,
  trackError,
} from 'utils/helpers';
import { removeAllFerryAndAssociatedVehicles } from 'redux/actions/reservation/delete';
import { setFerryGrouping } from 'redux/actions/ui/sealink';
import { fetchFamilies, fetchMakes } from 'redux/actions/vehicle_link';
import {
  getCurrentBooking,
  getOptionalExtras,
  getPrices,
} from 'redux/selectors/current_booking';
import { FerryView, FerryViewNoData } from './ferry_view';
import {
  getLuggageSummaryFromForm,
  getProductsNew,
  getVehicleDescription,
  getVehicleIcon,
} from './helpers';

const FerryForm = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  // Get settings of sealink from CMS
  const {
    vehicle_type_groups: cmsVehicleTypeGroups,
    vehicle_types: cmsVehicleTypes,
  } = useSelector((state) => state.sealink || {});

  const { ferryGrouping } = useSelector((state) => state.ui?.sealink);

  // Get settings from qt
  const {
    vehicleTypes: qtVehicleTypes = [],
    productTypes: qtProductTypes = [],
    passengerTypes: qtPassengerTypes = [],
  } = useSelector((state) => state.types || {});

  // Get settings of vehicleLink
  const {
    attachments = [],
    makes: vehicleMakes = [],
    families: vehicleFamilies = [],
  } = useSelector((state) => state.vehicleLink || {});

  const {
    passengerPrices,
    ferrySubtotalPrice,
    vehiclePrice,
    commissionPrice,
    commissionPercentage,
  } = useSelector(getPrices);

  const booking = useSelector(getCurrentBooking);
  const optionalExtras = useSelector(getOptionalExtras);
  const ferryReservations = getReservationsByProductType('ferry', booking);
  const hasFerryReservation = !isEmpty(ferryReservations);

  const customerFacingAdjustments = useMemo(() => {
    const groups = {};
    ferryReservations.forEach((reservation) => {
      (reservation.adjustments_attributes || [])
        .filter((adj) => adj.display_to_customer)
        .forEach((adj) => {
          const key = adj.adjustment_category_id;
          if (!groups[key]) {
            groups[key] = {
              category_id: key,
              display_name: adj.display_name,
              total: 0,
            };
          }
          groups[key].total += adj.gross_in_cents;
        });
    });
    return Object.values(groups);
  }, [ferryReservations]);
  const [isRemovingReservation, setIsRemovingReservation] = useState(false);

  // Fetch vehicle makes and families
  useEffect(() => {
    ferryGrouping.forEach((ferry) => {
      const { vehicleForm } = ferry.values;

      if (vehicleForm?.takingVehicle) {
        vehicleForm.vehicles.forEach((vehicle) => {
          if (
            vehicle.vehicleMake &&
            vehicle.vehicleMake !== HIGH_VOLUME_IMPORT_VEHICLE
          ) {
            const segmentCode = qtVehicleTypes.find(
              (item) => item.id === vehicle.vehicleTypeId
            )?.segment_code;
            if (segmentCode) {
              dispatch(fetchMakes(segmentCode));
              dispatch(
                fetchFamilies({
                  code: segmentCode,
                  makeCode: vehicle.vehicleMake,
                })
              );
            }
          }
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { luggageForm } = ferryGrouping[0]?.values || {};
  const luggages = useMemo(() => {
    if (luggageForm) {
      return getLuggageSummaryFromForm({
        formValues: luggageForm,
        luggageTypes: qtVehicleTypes,
      });
    }
  }, [luggageForm, qtVehicleTypes]);

  const ferries = ferryGrouping.map((ferry) => {
    const passengers = [];
    const vehicles = [];
    const {
      journeyForm,
      passengerForm,
      vehicleForm,

      departureServiceForm,
      returnServiceForm,
    } = ferry.values;
    const { journeyDates } = journeyForm;

    let products = {};

    products = getProductsNew({
      departureServiceForm,
      returnServiceForm,
      qtProductTypes,
    });

    // Build passenger data
    if (passengerForm) {
      Object.keys(passengerForm.passengers).forEach((type) => {
        const count = passengerForm.passengers[type];

        if (count) {
          const { name } = qtPassengerTypes[type];
          const passengerPrice = passengerPrices[name];

          passengers.push({
            name,
            count,
            price: passengerPrice,
            adjustments: [],
          });
        }
      });
    }

    // Build vehicle data
    if (vehicleForm?.takingVehicle) {
      vehicleForm.vehicles.forEach((vehicle) => {
        const vehicleData = {};
        vehicleData.name = getVehicleDescription({
          currentVehicle: vehicle,
          vehicleMakes,
          vehicleFamilies,
          cmsVehicleTypes,
          qtVehicleTypes,
        });
        vehicleData.icon = getVehicleIcon(vehicle, 'fas', {
          vehicle_type_groups: cmsVehicleTypeGroups,
          vehicle_types: cmsVehicleTypes,
        });
        vehicleData.price = price(vehiclePrice);

        if (vehicle.trailerRadio) {
          const vehicleType = cmsVehicleTypes.find(
            (vehicle_type) => vehicle_type.id === Number(vehicle.trailerRadio)
          );
          vehicleData.trailer = `+ ${vehicleType?.name || 'Trailer'} (${
            vehicle.trailerLength
          }m, ${vehicle.trailerHeight}m)`;
        }

        if (attachments?.length !== 0) {
          vehicleData.attachments = vehicle.attachmentIds?.map((id) => {
            const detail = attachments.find((item) => item.id === id);
            return {
              name:
                detail.category_id === 'roof'
                  ? `+ Roof luggage (${detail.description})`
                  : `+ ${detail.description}`,
            };
          });
        }

        vehicles.push(vehicleData);
      });
    }

    const result = {
      price: ferrySubtotalPrice,
      commissionPrice,
      commissionPercentage,
      departureFerry: {
        date: formatDate(journeyDates[0]),
        products: products.departureProduct || [],
      },
      passengers,
      optionalExtras,
      customerFacingAdjustments,
      vehicles: !isEmpty(luggages)
        ? [
            {
              icon: 'fas fa-suitcase',
              name: 'Additional Items',
              price: price(vehiclePrice),
              attachments: luggages,
            },
          ]
        : vehicles,
    };

    if (journeyDates[1]) {
      result.returnFerry = {
        date: formatDate(journeyDates[1]),
        products: products.returnProduct || [],
      };
    }

    return result;
  });

  const handleRemoveFerryBooking = () => {
    if (hasFerryReservation) {
      setIsRemovingReservation(true);
      dispatch(removeAllFerryAndAssociatedVehicles())
        .then(() => {
          dispatch(setFerryGrouping([]));
          setIsRemovingReservation(false);
          history.push('/bookings');
        })
        .catch((error) => {
          trackError(error);
          setIsRemovingReservation(false);
        });
    } else {
      dispatch(setFerryGrouping([]));
      history.push('/bookings');
    }
  };

  const actions = {
    editFerry: (url) => history.push(url),
    removeFerry: handleRemoveFerryBooking,
  };

  return (
    <>
      {ferries.length ? (
        <>
          {/* eslint-disable react/no-array-index-key */}
          {ferries.map((ferry, index) => (
            <FerryView
              ferry={ferry}
              actions={actions}
              key={index}
              isDeletingReservation={isRemovingReservation}
            />
          ))}
        </>
      ) : (
        <FerryViewNoData />
      )}
    </>
  );
};

export default FerryForm;
